import { useEffect, useState } from 'react';

import { useNavigateWithTransition } from '@muzly/shared';

import { loadProfile } from '../../profile/profile';
import { useProfileAnswerSync } from '../../profile/useProfileAnswerSync';
import {
  DiagnosisIntroStep,
  ResultView,
  StepShell,
  SurveyStep,
  VoiceRecordStep,
} from '../components';
import { OnboardingProvider, useOnboarding } from '../context';
import { DIAGNOSIS_FLOW } from '../flow';
import { loadProgress } from '../record';
import { draftStore } from '../storage';
import { isSurveyStepAnswered, pickAnswers, surveyStepById } from '../survey';

import type { OnboardingStepId } from '../flow';
import type { SurveyStepConfig } from '../survey';

/**
 * 진단 회차의 루트 페이지 — 프로바이더 경계이자 단계 오케스트레이터.
 * DIAGNOSIS_FLOW의 모든 단계가 실제 화면을 렌더한다. 자체 전이(내부 버튼)를 가진
 * 단계(녹음·결과)는 StepShell 기본 CTA를 숨긴다.
 *
 * 설문은 여기 없다 — 초기 설정(`/setup`)이 프로필로 남긴 답을 **밑에 깔고** 시작한다
 * (`initialAnswers`). 엔진(`reportFrom`)과 회차 저장(`toDiagnosisRecord`)은 그 합쳐진
 * `answers`만 본다.
 */

const store = draftStore(DIAGNOSIS_FLOW);

/**
 * 끝난 회차의 드래프트로 다시 들어오면 결과 화면이 열린다 — 홈의 "진단하기"가 지난
 * 결과를 다시 보여주는 막다른 길이 된다. 결과 화면은 세션 안에서만 열리고 나가는 길이
 * 드래프트를 지우지만, 결과를 보다 새로고침한 사용자는 그 길을 안 탔다.
 *
 * 판정: **진행이 없는데 리포트가 남아 있다 = 그 회차는 끝났다.** 진행이 있으면
 * 이어서 하는 중이므로 그대로 둔다. 끝난 회차의 드래프트는 통째로 버린다 — 회차 답
 * (환경·컨디션)은 어차피 다시 묻고, 성별·나이는 프로필이 갖고 있어 잃을 것이 없다.
 *
 * 프로바이더가 드래프트를 읽기 **전에** 돌아야 해서 부모 본문에서 한 번 실행한다.
 * 두 번 돌아도 안전하다 — 첫 실행이 지우면 다음은 곧바로 빠져나간다.
 */
const restartFinishedRun = (): void => {
  const draft = store.load();
  if (draft?.report && !loadProgress()) {
    store.clear();
  }
};

const SELF_ADVANCING: ReadonlySet<OnboardingStepId> = new Set([
  // 진단 안내는 2장짜리 팝업이라 자기 "진단하기" 버튼으로 넘어간다 — 기본 CTA를 같이
  // 두면 안내를 다 읽기 전에 넘길 수 있는 두 번째 문이 생긴다.
  'diagnosisIntro',
  'record',
  'result',
]);

const stepContent = (
  step: OnboardingStepId,
  survey: SurveyStepConfig | undefined,
) => {
  if (survey) {
    return <SurveyStep config={survey} />;
  }
  switch (step) {
    case 'diagnosisIntro':
      return <DiagnosisIntroStep />;
    case 'record':
      return <VoiceRecordStep />;
    case 'result':
      return <ResultView />;
    default:
      return null;
  }
};

/**
 * 회차마다 다시 묻는 답 — 서버 프로필에서는 이 둘도 프로필 필드다(진단 레코드에는
 * 안 담긴다). 로컬 프로필(`PROFILE_KEYS`)에는 없으므로 회차가 시작될 때 여기서 올린다.
 */
const ROUND_PROFILE_KEYS = ['environment', 'condition'] as const;

const OnboardingSteps = () => {
  const { step, answers } = useOnboarding();
  const survey = surveyStepById(step);
  const canAdvance = survey ? isSurveyStepAnswered(survey, answers) : true;
  const pushToServer = useProfileAnswerSync(0);

  // 녹음 직전에 한 번. 서버가 진단 레코드의 피로도 필드를 폐기하고 프로필 condition으로
  // 대체했으므로, 갱신하지 않으면 서버가 보는 컨디션이 첫 회차에 멈춘다.
  useEffect(() => {
    if (step !== 'record') {
      return;
    }
    pushToServer(pickAnswers(answers, ROUND_PROFILE_KEYS));
    // 회차당 한 번 — 답이 바뀌면 그건 다음 회차다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <StepShell
      canAdvance={canAdvance}
      // 회차 질문의 CTA는 시안대로 `계속`이다 — `다음`은 안내 화면의 문구다.
      ctaLabelKey={survey ? 'web.common.continue_' : undefined}
      hideCta={SELF_ADVANCING.has(step)}
      // 측정 화면은 가장자리까지 차야 한다 — 셸의 여백과 크롬을 뺀다. 같은 단계의 다른
      // 화면(가이드·에러·완료)은 VoiceRecordStep이 자기 여백으로 감싼다.
      bare={step === 'record'}
    >
      {stepContent(step, survey)}
    </StepShell>
  );
};

export const OnboardingPage = () => {
  const navigate = useNavigateWithTransition();
  // 마운트 때 딱 한 번, 프로바이더의 지연 초기화보다 먼저.
  useState(restartFinishedRun);
  // 프로필도 열 때 한 번 읽는다 — 회차 도중 프로필이 바뀔 일은 없다(더보기는 다른 화면).
  const [profileAnswers] = useState(() => loadProfile()?.answers ?? {});

  return (
    <OnboardingProvider
      flow={DIAGNOSIS_FLOW}
      initialAnswers={profileAnswers}
      // 나가는 길은 홈이다. 회차는 이미 저장됐고(`diagnosisStore`) 프로필은 별도 키라, 이
      // 회차의 드래프트는 지워도 잃는 것이 없다 — 지워야 다음 `진단하기`가 새 회차를 연다.
      onComplete={() => {
        store.clear();
        navigate('/', { replace: true, transition: true, direction: 'back' });
      }}
    >
      <OnboardingSteps />
    </OnboardingProvider>
  );
};
